import React, { useMemo } from "react";
import { Alert, Button, Grid, MenuItem, Typography } from "@mui/material";
import { Field, Form, Formik, FormikProps } from "formik";
import { TextField } from "formik-mui";
import styled from "styled-components";
import { useTranslation } from "react-i18next";

import { APIError, Segment, TimefactorType, type Timefactor } from "src/types";
import { VARIATION_VARIABLE } from "src/config";
import { adornments } from "src/config/adornments";
import { useSegmentBulkSchema } from "src/schemas/segment-bulk.schema";
import { ButtonGrid } from "src/components/form-components/ButtonGrid";
import { ButtonLoading } from "src/components/ButtonLoading";
import { ServerErrors } from "src/components/form-components/ServerErrors";
import { ButtonDocs } from "src/components/ButtonDocs";
import { SegmentBulkMutationData } from "src/hooks/segments/useSegmentBulkUpdateMutation";
import { toInitialValues, unprepareVehicleCounts } from "src/utils/segments-bulk-expression.utils";
import {
  propertyValue,
  propertyVehicleCountsValue,
  helperTextValue,
  helperTextVehicleCountsValue,
  helperTextValueWithOptions,
} from "src/utils/segment-bulk-form.utils";

export type SegmentBulkFormData = {
  roadType: string | number;
  height_m: string | number;
  speed_km_h: string | number;
  vehicleCounts: Array<{
    vehicleType: string;
    count: string | number;
  }>;
};

interface Props {
  title?: string;
  readOnly?: boolean;
  segments: Segment[];
  initialValues: SegmentBulkMutationData | null;
  timefactorType: TimefactorType;
  vehicleTypes: string[];
  roadTypes: string[];
  timefactors: Timefactor[];
  onClose: () => void;
  onSubmit: (values: SegmentBulkFormData) => any;
  onDelete?: () => void;
  isLoading: boolean;
  errors?: APIError[] | null;
  formRef?: React.MutableRefObject<FormikProps<SegmentBulkFormData> | null>;
}

export function SegmentBulkForm(props: Props) {
  const { t } = useTranslation();
  const schema = useSegmentBulkSchema();

  const initialValues: SegmentBulkFormData = useMemo(() => {
    if (props.initialValues) {
      return unprepareVehicleCounts(
        toInitialValues<SegmentBulkFormData & { vehicleCounts: { [key: string]: string | number } }>(
          props.initialValues.propertyExpressionList
        )
      );
    }
    return {
      roadType: propertyValue(props.segments, "roadType"),
      ifdmTimeFactor: propertyValue(props.segments, "ifdmTimeFactor"),
      height_m: propertyValue(props.segments, "height_m"),
      speed_km_h: propertyValue(props.segments, "speed_km_h"),
      vehicleCounts: props.vehicleTypes.map((vehicleType) => {
        return {
          vehicleType,
          count: propertyVehicleCountsValue(props.segments, vehicleType),
        };
      }),
    };
  }, [props.initialValues, props.segments, props.vehicleTypes]);

  return render();

  function render() {
    return (
      <Formik
        innerRef={props.formRef}
        initialValues={initialValues}
        validationSchema={schema}
        enableReinitialize
        onSubmit={async (values: SegmentBulkFormData) => {
          return props.onSubmit(values);
        }}
      >
        {({ isSubmitting }) => (
          <Form>
            <Grid container spacing={3}>
              {props.title && (
                <Grid item xs={12}>
                  <Typography variant="h2">{props.title}</Typography>
                </Grid>
              )}

              <Grid item xs={12}>
                <ButtonDocs docsPath="/tutorial/scenario/scenario-emissions#bulk-edit-segments">
                  {t("label.moreInfoAboutBulkEdit")}
                </ButtonDocs>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body1">{t("label.trafficCount")}</Typography>
                <Typography variant="body2" color="textSecondary">
                  {t("label.timefactorType")}: {t(`label.timefactorTypes.${props.timefactorType}`)}
                </Typography>
              </Grid>

              {props.vehicleTypes.map((vehicleType, i) => {
                return (
                  <Grid item xs={12} key={["vehicleCounts", vehicleType].join()}>
                    <Field
                      component={TextField}
                      name={`vehicleCounts[${i}].count`}
                      label={t(`label.vehicleType.${vehicleType}`)}
                      variant="outlined"
                      fullWidth
                      disabled={props.readOnly}
                      helperText={helperTextVehicleCountsValue(props.segments, vehicleType, t)}
                    />
                  </Grid>
                );
              })}

              <Grid item xs={12}>
                <Typography>{t("label.segmentProperties")}</Typography>
              </Grid>

              <Grid item xs={12}>
                <Field
                  component={TextField}
                  name="speed_km_h"
                  label={t("label.speedLimit")}
                  variant="outlined"
                  InputProps={adornments(t).kmPerHour}
                  fullWidth
                  disabled={props.readOnly}
                  helperText={helperTextValue(props.segments, "roadTypeOld", t)}
                />
              </Grid>

              <Grid item xs={12}>
                <Field
                  component={TextField}
                  name="height_m"
                  label={t("label.height")}
                  variant="outlined"
                  InputProps={adornments(t).meter}
                  fullWidth
                  disabled={props.readOnly}
                  helperText={helperTextValue(props.segments, "heightOld_m", t)}
                />
              </Grid>
              <Grid item xs={12}>
                <SelectField
                  data={props.segments}
                  options={props.roadTypes.map((roadType) => ({
                    value: roadType,
                    label: t(`label.roadTypeDict.${roadType}`),
                  }))}
                  name="roadType"
                  label={t("label.roadType")}
                  property="roadType"
                  oldProperty="roadTypeOld"
                  readOnly={props.readOnly}
                />
              </Grid>
              <Grid item xs={12}>
                <SelectField
                  name="ifdmTimeFactor"
                  label={t("label.timefactor")}
                  data={props.segments}
                  options={props.timefactors.map((t) => ({
                    value: t.nameKey,
                    label: t.nameTranslation,
                  }))}
                  property="ifdmTimeFactor"
                  oldProperty="ifdmTimeFactorOld"
                  readOnly={props.readOnly}
                />
              </Grid>

              {props.errors && props.errors.length > 0 && (
                <Grid item xs={12}>
                  <Alert severity="error" icon={false}>
                    t('label.bulkEditValidationErrorsWithoutDefaultValues')
                  </Alert>
                  <ServerErrors data={props.errors} />
                </Grid>
              )}
              {!props.readOnly && (
                <>
                  <ButtonGrid container item xs={12} sx={{ justifyContent: "flex-end" }}>
                    <Button variant="text" onClick={props.onClose}>
                      {t("label.cancel")}
                    </Button>
                    <ButtonLoading
                      isLoading={isSubmitting || props.isLoading}
                      type="submit"
                      color="primary"
                      variant="contained"
                    >
                      {t("label.save")}
                    </ButtonLoading>
                  </ButtonGrid>

                  <ButtonGrid container item xs={12} sx={{ justifyContent: "flex-end" }}>
                    {props.onDelete && (
                      <DeleteButton variant="text" onClick={props.onDelete}>
                        {t("label.deleteAll")}
                      </DeleteButton>
                    )}
                  </ButtonGrid>
                </>
              )}

              {props.readOnly && (
                <ButtonGrid container item xs={12} sx={{ justifyContent: "flex-end" }}>
                  <Button variant="text" onClick={props.onClose}>
                    {t("label.close")}
                  </Button>
                </ButtonGrid>
              )}
            </Grid>
          </Form>
        )}
      </Formik>
    );
  }
}

function RoadTypeField(props: { data: Segment[]; roadTypes: string[]; readOnly?: boolean }) {
  const { t } = useTranslation();

  const options = useMemo(() => {
    const roadType = propertyValue(props.data, "roadType");
    if (roadType === VARIATION_VARIABLE) return [VARIATION_VARIABLE, ...props.roadTypes];
    else return props.roadTypes;
  }, [props.data, props.roadTypes]);

  function label(option: string | number) {
    if (option === VARIATION_VARIABLE) return option;
    else return t(`label.roadTypeDict.${option}`);
  }

  return render();

  function render() {
    return (
      <Field
        component={TextField}
        name="roadType"
        label={t("label.roadType")}
        type="text"
        select={true}
        variant="outlined"
        fullWidth
        disabled={props.readOnly}
        helperText={helperTextValue(props.data, "roadTypeOld", t)}
      >
        {options.map((option) => (
          <MenuItem key={option} value={option}>
            {label(option)}
          </MenuItem>
        ))}
      </Field>
    );
  }
}

const DeleteButton = styled(Button)`
  color: ${({ theme }) => theme.palette.error.main};
`;

function SelectField(props: {
  data: Segment[];
  options: { value: string; label: string }[];
  name: string;
  label: string;
  property: "ifdmTimeFactor" | "roadType";
  oldProperty: "ifdmTimeFactorOld" | "roadTypeOld";
  readOnly?: boolean;
}) {
  const { t } = useTranslation();

  const mergedOptions = useMemo(() => {
    const value = propertyValue(props.data, props.property);
    if (value === VARIATION_VARIABLE)
      return [{ value: VARIATION_VARIABLE, label: VARIATION_VARIABLE }, ...props.options];
    return props.options;
  }, [props.data, props.options, props.property]);

  function label(option: (typeof props.options)[number]) {
    if (option.value === VARIATION_VARIABLE) return option.value;
    return option.label;
  }

  return render();

  function render() {
    return (
      <Field
        component={TextField}
        name={props.name}
        label={props.label}
        type="text"
        select={true}
        variant="outlined"
        fullWidth
        disabled={props.readOnly}
        helperText={helperTextValueWithOptions(props.data, props.oldProperty, props.options, t)}
      >
        {mergedOptions.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {label(option)}
          </MenuItem>
        ))}
      </Field>
    );
  }
}
